import { createElement } from "react";
import { Navigate } from "react-router-dom";
import {
  IconDashboard,
  IconUsers,
  IconBuilding,
  IconBriefcase,
  IconAlertCircle,
  IconClipboardCheck,
  IconPill,
  IconCertificate,
  IconListCheck,
} from "@tabler/icons-react";
import { lazyNamed } from "@/lib/lazyNamed";
// RoleBasedRedirect is the eager index element (tiny, needed immediately).
import { RoleBasedRedirect } from "@/components/RoleBasedRedirect";
// settingsRoute/profileRoute moved to @livingsyncs/ui (frontend split epic
// Phase 5, Task 5.2 follow-up) — a real-caller audit confirmed both are
// genuinely shared (admin.portal.ts imports both; audit.portal.ts imports
// profileRoute). notificationsRoute stayed here: despite its own comment
// below claiming the same "shared across app, admin, and audit" status,
// neither admin.portal.ts nor audit.portal.ts actually import it — verified
// directly, not assumed from the comment.
// Imported via the deep path, not the main @livingsyncs/ui barrel:
// sharedRoutes.ts pulls in ProfilePage.tsx, which has its own disclosed,
// unresolved gap (ProfileSettingsPage -> StaffFormsSection's unaudited
// tree) — see @livingsyncs/ui's index.ts for why that gap must not be
// reachable from the main barrel every app imports unconditionally (it
// broke frontend's own tsc-gated build the first time this was tried).
import {
  settingsRoute,
  profileRoute,
} from "@livingsyncs/ui/src/router/sharedRoutes";
import type { PortalDef } from "../types";

const AppLayout = lazyNamed(
  () => import("@/components/AppLayout"),
  "AppLayout",
);

// ── Lazy pages ───────────────────────────────────────────────────────────────
const SupervisedSessionPage = lazyNamed(
  () => import("@/pages/SupervisedSessionPage"),
  "SupervisedSessionPage",
);
const DashboardPage = lazyNamed(
  () => import("@/pages/DashboardPage"),
  "DashboardPage",
);
const ResidentsPage = lazyNamed(
  () => import("@/pages/ResidentsPage"),
  "ResidentsPage",
);
const BillingPackPage = lazyNamed(
  () => import("@/pages/BillingPackPage"),
  "BillingPackPage",
);
const ResidentDetailPage = lazyNamed(
  () => import("@/pages/ResidentDetailPage"),
  "ResidentDetailPage",
);
const ResidentForm = lazyNamed(
  () => import("@/features/residents/ResidentForm"),
  "ResidentForm",
);
const ResidentFormRequirementDetailPage = lazyNamed(
  () => import("@/pages/residents/forms/ResidentFormRequirementDetailPage"),
  "ResidentFormRequirementDetailPage",
);
const AppointmentRedirectPage = lazyNamed(
  () => import("@/pages/AppointmentRedirectPage"),
  "AppointmentRedirectPage",
);
const IncomingTransfersPage = lazyNamed(
  () => import("@/pages/IncomingTransfersPage"),
  "IncomingTransfersPage",
);
const RequestsPage = lazyNamed(
  () => import("@/pages/RequestsPage"),
  "RequestsPage",
);
const FacilitiesPage = lazyNamed(
  () => import("@/pages/FacilitiesPage"),
  "FacilitiesPage",
);
const GroupHomeOverviewPage = lazyNamed(
  () => import("@/pages/GroupHomeOverviewPage"),
  "GroupHomeOverviewPage",
);
const FacilityDetailPage = lazyNamed(
  () => import("@/pages/FacilityDetailPage"),
  "FacilityDetailPage",
);
const EmployeesPage = lazyNamed(
  () => import("@/pages/EmployeesPage"),
  "EmployeesPage",
);
const EmployeeDetailPage = lazyNamed(
  () => import("@/pages/EmployeeDetailPage"),
  "EmployeeDetailPage",
);
const IncidentsPage = lazyNamed(
  () => import("@/pages/IncidentsPage"),
  "IncidentsPage",
);
const IncidentDetailPage = lazyNamed(
  () => import("@/pages/IncidentDetailPage"),
  "IncidentDetailPage",
);
const MedicationReviewPage = lazyNamed(
  () => import("@/pages/MedicationReviewPage"),
  "MedicationReviewPage",
);
const MedicationsDuePage = lazyNamed(
  () => import("@/pages/MedicationsDuePage"),
  "MedicationsDuePage",
);
const CarePlansPage = lazyNamed(
  () => import("@/pages/CarePlansPage"),
  "CarePlansPage",
);
const TaskCompletionPage = lazyNamed(
  () => import("@/pages/TaskCompletionPage"),
  "TaskCompletionPage",
);
const MyTasksPage = lazyNamed(
  () => import("@/pages/MyTasksPage"),
  "MyTasksPage",
);
const CalendarPage = lazyNamed(
  () => import("@/pages/CalendarPage"),
  "CalendarPage",
);
const CaseList = lazyNamed(() => import("@/pages/Cases/CaseList"), "CaseList");
const CaseDetail = lazyNamed(
  () => import("@/pages/Cases/CaseDetail"),
  "CaseDetail",
);
const FormsListPage = lazyNamed(
  () => import("@/pages/forms/FormsListPage"),
  "FormsListPage",
);
const FormPreviewPage = lazyNamed(
  () => import("@/pages/forms/FormPreviewPage"),
  "FormPreviewPage",
);
const FormSubmissionPage = lazyNamed(
  () => import("@/pages/forms/FormSubmissionPage"),
  "FormSubmissionPage",
);
const FormSubmissionsPage = lazyNamed(
  () => import("@/pages/forms/FormSubmissionsPage"),
  "FormSubmissionsPage",
);
const FormSubmissionDetailPage = lazyNamed(
  () => import("@/pages/forms/FormSubmissionDetailPage"),
  "FormSubmissionDetailPage",
);
const AuditManagementPage = lazyNamed(
  () => import("@/pages/AuditManagementPage"),
  "AuditManagementPage",
);
const TrainingPage = lazyNamed(
  () => import("@/pages/TrainingPage"),
  "TrainingPage",
);
const TrainingMaterialViewer = lazyNamed(
  () => import("@/features/training/TrainingMaterialViewer"),
  "TrainingMaterialViewer",
);
const AssessmentForm = lazyNamed(
  () => import("@/features/training/AssessmentForm"),
  "AssessmentForm",
);
const AssignTrainingForm = lazyNamed(
  () => import("@/features/training/AssignTrainingForm"),
  "AssignTrainingForm",
);
const AppointmentFormRulesPage = lazyNamed(
  () => import("@/pages/group-home/AppointmentFormRulesPage"),
  "AppointmentFormRulesPage",
);
const RuleLibraryBrowsePage = lazyNamed(
  () => import("@/pages/group-home/rule-library/RuleLibraryBrowsePage"),
  "RuleLibraryBrowsePage",
);
// US-134: org-level setting defaults (reminder policy + automations).
const GroupHomeDefaultsPage = lazyNamed(
  () => import("@/pages/settings/GroupHomeDefaultsPage"),
  "GroupHomeDefaultsPage",
);
const NotificationsPage = lazyNamed(
  () => import("@/pages/NotificationsPage"),
  "NotificationsPage",
);
const WebhooksPage = lazyNamed(
  () => import("@/pages/settings/WebhooksPage"),
  "WebhooksPage",
);
const WebhookDeliveriesPage = lazyNamed(
  () => import("@/pages/settings/WebhookDeliveriesPage"),
  "WebhookDeliveriesPage",
);

const LegalDocumentsPage = lazyNamed(
  () => import("@/pages/settings/LegalDocumentsPage"),
  "LegalDocumentsPage",
);

// Standalone notifications feed (the bell dropdown's "View all notifications"
// target). Reached from the header bell rather than the sidebar, so it is shared
// across the app, admin, and audit portals like profileRoute.
const notificationsRoute = {
  path: "notifications",
  Component: NotificationsPage,
};

/**
 * The App portal authored as data (US-044 AC6). Faithful translation of the
 * previous `/` `AppLayout` subtree:
 *   - `ManagerRoute` → `access: { anyOf: ["staff:manage"] }`
 *   - `ShiftRequiredRoute` → `access: { shiftRequired, shiftPageName }`
 *   - `NonAdminRoute` (and unwrapped routes) → no access; `PortalBoundary`
 *     handles app-portal membership.
 * Admin routes moved out to `admin.portal.ts` (their own `/admin` portal).
 */
export const appPortal: PortalDef = {
  id: "app",
  basePath: "/",
  layout: AppLayout,
  routes: [
    { index: true, element: createElement(RoleBasedRedirect) },

    // Personal account page, reached from the header account menu (not sidebar).
    profileRoute,

    // Notifications feed, reached from the header bell's "View all" link.
    notificationsRoute,

    // Platform requests (form-template asks) — reached from the header launcher
    // menu. Gated in-page on request:write (mirrors the Forms "Request a Form").
    { path: "requests", Component: RequestsPage },

    // US-029: supervised iPad session for external-provider forms.
    {
      path: "external-providers/:providerId/assignments/:assignmentId/supervised",
      Component: SupervisedSessionPage,
    },

    {
      path: "dashboard",
      Component: DashboardPage,
      nav: { label: "Dashboard", icon: IconDashboard, order: 1 },
    },
    {
      path: "residents",
      Component: ResidentsPage,
      // US-063 — resident:read to enter the residents surface (advisory UX; the
      // backend /residents decide() is authoritative).
      access: { resource: "resident" },
      nav: { label: "Residents", icon: IconUsers, order: 2 },
    },
    // US-063: the create/edit form writes the resident + cascades emergency-contacts
    // (emergency-contact:write). Gate entry on resident:write so a read-only viewer
    // can't open a form that will 403 on submit. (anyOf on the write key — RouteAccess
    // has no read/write verb override.)
    {
      path: "residents/new",
      Component: ResidentForm,
      access: { anyOf: ["resident:write"] },
    },
    {
      path: "residents/:id/edit",
      Component: ResidentForm,
      access: { anyOf: ["resident:write"] },
    },
    {
      path: "residents/:id/form-requirements/:reqId",
      Component: ResidentFormRequirementDetailPage,
      access: { resource: "form-requirement" },
    },
    {
      path: "residents/:id",
      Component: ResidentDetailPage,
      // US-063 — resident:read to enter; shiftRequired still delegates to the
      // on-shift modal.
      access: {
        resource: "resident",
        shiftRequired: true,
        shiftPageName: "Resident Details",
      },
    },
    // Deep-link resolver only — not a nav/link target. Notification emails
    // (external-provider assignment reminders) and dashboard widgets only
    // carry a bare appointment id; this looks it up and redirects into the
    // owning resident's Calendar tab. No in-app code should ever construct
    // this URL directly (opted out in routePaths.test.ts's
    // INTENTIONALLY_UNBUILT, same precedent as /incoming-transfers).
    {
      path: "appointments/:appointmentId",
      Component: AppointmentRedirectPage,
      access: { resource: "appointment" },
    },
    // Cross-org transfer acceptance queue now lives as a Settings tab
    // (/settings/incoming-transfers). Keep the old top-level path as a redirect
    // so transfer.requested notification deep-links don't break; access is kept
    // so a non-holder hits the permission boundary rather than a silent bounce.
    {
      path: "incoming-transfers",
      element: createElement(Navigate, {
        to: "/settings/incoming-transfers",
        replace: true,
      }),
      access: { anyOf: ["resident-transfer:accept"] },
    },
    {
      path: "facilities",
      Component: FacilitiesPage,
      nav: { label: "Facilities", icon: IconBuilding, order: 3 },
    },
    // US-144 — a dedicated destination, not a facility tab: billing is a
    // monthly CROSS-facility ritual, so it must not be scoped to one facility's
    // page. Deliberately NOT in the sidebar: it is a secondary destination, and
    // those live in the AppLayout quick-access launcher. It is also reachable
    // from the Facilities kebab, which deep-links `?facility=<id>` to
    // preselect. RouteGuard still enforces `access` on direct navigation.
    {
      path: "billing-pack",
      Component: BillingPackPage,
      access: { anyOf: ["billing-pack:read"] },
    },
    { path: "facilities/:id", Component: FacilityDetailPage },
    // Group Home Overview — org-wide (cross-facility) roll-up. Reached from the
    // Facilities page header (not the sidebar). No `access` gate: like
    // `facilities`, it's advisory UX and its data hooks scope to
    // `context.facilityIds` server-side.
    { path: "group-home", Component: GroupHomeOverviewPage },
    {
      path: "employees",
      Component: EmployeesPage,
      // US-062: the employees surface stays a management surface — gated on the
      // employee:write resource key (org/facility managers), matching the prior
      // staff:manage gate exactly. (RouteAccess has no read/write override, so the
      // managers-only intent is expressed via anyOf on the write key.)
      access: { anyOf: ["employee:write"] },
      // Manager-only nav comes from the route access above.
      nav: { label: "Employees", icon: IconBriefcase, order: 4 },
    },
    {
      path: "employees/:id",
      Component: EmployeeDetailPage,
      access: { anyOf: ["employee:write"] },
    },
    {
      path: "incidents",
      Component: IncidentsPage,
      nav: { label: "Incidents", icon: IconAlertCircle, order: 5 },
    },
    { path: "incidents/:id", Component: IncidentDetailPage },
    {
      path: "medications-due",
      Component: MedicationsDuePage,
      // US-052 — medication:read to enter (the MAR is a read view); shiftRequired
      // still delegates to the on-shift modal. resource is advisory UX; the backend
      // /facilities/[id]/medications/due decide() is authoritative.
      access: {
        resource: "medication",
        shiftRequired: true,
        shiftPageName: "Medication Pass Sheet",
      },
      // shiftRequired does not hide the link (runtime gate) — shown to all.
      nav: { label: "Med Pass", icon: IconPill, order: 8 },
    },
    {
      path: "medication-review",
      Component: MedicationReviewPage,
      // US-084 RG-7 — the pharmacy-order review queue. Approval needs
      // medication:write; the queue itself is readable by the medication
      // cohort (backend decide() is authoritative). Deliberately NOT in the
      // sidebar — reached from the Med Pass header button (pending badge)
      // and notification deep links; one nav entry per domain.
      access: { anyOf: ["medication:write"] },
    },
    {
      path: "care-plans",
      Component: CarePlansPage,
      // Deliberately NOT in the sidebar — the care-plan review queue is reached
      // from the care_plan_review_reminder notification deep link (one entry per
      // domain, mirroring the Med Pass monitor route above). Route-gated to the
      // clinical cohort; the page also self-guards on care-plan:read.
      access: { anyOf: ["care-plan:read", "case:read"] },
    },
    // US-108: staff "My Tasks" worklist (per-assignee + role-pool stateful tasks
    // they can act on). Self-service — visible to all app-portal users; the page
    // is empty for accounts without an employee record.
    {
      path: "my-tasks",
      Component: MyTasksPage,
      nav: {
        label: "My Tasks",
        icon: IconListCheck,
        order: 10,
      },
    },
    // Task completion analytics — due-cohort completion/on-time rates over the
    // engine's task_instances. analytics:read (managers/org-admins via
    // analytics:write→read); care/medical staff lack it, so the per-staff
    // accountability table is never peer-visible. Deliberately NOT in the
    // sidebar — reached from the "Task Analytics" button on the My Tasks page
    // (one entry per domain). Page also self-guards on analytics:read.
    {
      path: "task-completion",
      Component: TaskCompletionPage,
      access: { anyOf: ["analytics:read"] },
    },
    // Calendar / Forms / Training / Audit Management moved off the sidebar into
    // the header launcher menu (AppLayout). Routes (and access) kept.
    {
      path: "calendar",
      Component: CalendarPage,
      // US-060: viewing the calendar requires calendar-event:read (all app staff hold it).
      access: { resource: "calendar-event" },
    },

    // Case Management
    {
      path: "cases",
      Component: CaseList,
      // US-055 — case:read to enter (advisory UX; backend decide() is authoritative).
      access: { resource: "case" },
      nav: { label: "Cases", icon: IconClipboardCheck, order: 6 },
    },
    { path: "cases/:id", Component: CaseDetail, access: { resource: "case" } },

    // Forms (user portal) — nav in the header launcher menu.
    { path: "forms", Component: FormsListPage },
    { path: "forms/:id/preview", Component: FormPreviewPage },
    {
      path: "forms/:templateId/submit",
      Component: FormSubmissionPage,
      access: { shiftRequired: true, shiftPageName: "Form Submission" },
    },
    { path: "forms/submissions", Component: FormSubmissionsPage },
    { path: "forms/submissions/:id", Component: FormSubmissionDetailPage },

    // Audit management (group-home managers). The single manager entry point:
    // requests, active audits, findings (respond + bulk), and reports all live
    // as tabs here. Nav lives in the header launcher menu (staff:manage gated).
    // `/audit-findings` is kept as a redirect for old links.
    {
      path: "audit-management",
      Component: AuditManagementPage,
      access: { anyOf: ["audit:read"] },
    },
    {
      path: "audit-findings",
      element: createElement(Navigate, {
        to: "/audit-management",
        replace: true,
      }),
      access: { anyOf: ["audit:read"] },
    },

    // Training & LMS. US-059: resource-scoped entry gates (advisory UX; backend
    // decide() is authoritative). Entering a route requires `${resource}:read`.
    // SCRUM-113: surfaced in the sidebar (was only in the header launcher menu,
    // so Facility Managers couldn't find it). Nav gated by the route resource —
    // anyone with training-material:read sees it.
    {
      path: "training",
      Component: TrainingPage,
      access: { resource: "training-material" },
      // order 11: My Tasks (US-108) takes slot 10 — the personal worklist ranks
      // above Training for daily staff use; deconflicts the previous 10/10 tie.
      nav: { label: "Training", icon: IconCertificate, order: 11 },
    },
    {
      path: "training/:materialId",
      Component: TrainingMaterialViewer,
      access: { resource: "training-material" },
    },
    {
      // Taking an assessment is consuming a training material — staff hold
      // training-material:read. The submit endpoint is selfService (no resource
      // gate); training-completion:read is manager-only and would lock learners out.
      path: "training/:materialId/assessment",
      Component: AssessmentForm,
      access: { resource: "training-material" },
    },
    {
      path: "training/assign",
      Component: AssignTrainingForm,
      access: { resource: "training-assignment" },
    },

    // US-032 / US-035 → consolidated into Settings tabs. Appointment Form Rules
    // and Rule Library are now Settings tabs; Appointment Types is a drawer on
    // the Form Rules tab (rules reference types). Old deep links redirect to the
    // new homes; access is kept so a non-manager still hits the permission
    // boundary rather than being silently bounced.
    {
      path: "group-home/appointment-types",
      element: createElement(Navigate, {
        to: "/settings/appointment-form-rules",
        replace: true,
      }),
      access: { anyOf: ["appointment-type-definition:write"] },
    },
    {
      path: "group-home/appointment-form-rules",
      element: createElement(Navigate, {
        to: "/settings/appointment-form-rules",
        replace: true,
      }),
      access: { anyOf: ["appointment-form-rule:write"] },
    },
    {
      path: "group-home/rule-library",
      element: createElement(Navigate, {
        to: "/settings/rule-library",
        replace: true,
      }),
      // US-156 (F-050): was rule-bundle:write, which the generated defaults give
      // to role:platform:admin ONLY — while rule-bundle:read goes to org admins
      // too. So the bookmark sent an org admin to /no-access even though they
      // can use the destination by typing the URL. A redirect must not demand
      // more than the page it redirects to.
      access: { anyOf: ["rule-bundle:read"] },
    },

    // Settings — manager/admin surface only (personal settings moved to the
    // Profile page). Gated so basic care/medical staff neither see the sidebar
    // link nor can reach it by URL; every settings audience holds at least one
    // of these keys (org admins + facility managers have staff:manage; auditors
    // / facility managers have admin:audit_logs). The app portal also adds two
    // group-home config tabs (US-032/US-035) here only (not admin/audit).
    {
      ...settingsRoute,
      access: {
        anyOf: ["employee:write", "audit:read"],
      },
      nav: {
        ...settingsRoute.nav,
        anyOf: ["employee:write", "audit:read"],
      },
      children: [
        ...settingsRoute.children,
        { path: "appointment-form-rules", Component: AppointmentFormRulesPage },
        {
          // US-156 (F-056): declared no `access`, so it inherited the settings
          // parent's employee:write | audit:read — while GET /api/rule-bundles
          // declares { resource: "rule-bundle" }, deriving rule-bundle:read.
          // A facility manager holds employee:write and no rule-bundle key, so
          // the route admitted them, the page chrome rendered, and the API 403'd
          // into "Failed to load the rule library." Name the key the API needs.
          path: "rule-library",
          Component: RuleLibraryBrowsePage,
          access: { anyOf: ["rule-bundle:read"] },
        },
        // US-134: org default reminder policy + automations, each with an
        // "allow facilities to override" flag. Same scope as the tabs above.
        { path: "group-defaults", Component: GroupHomeDefaultsPage },
        // US-143 — the organization's archive of executed agreements (doc 11
        // §10 promises signed records stay accessible to the parties). APP
        // PORTAL ONLY: admin.portal.ts reuses settingsRoute, and a platform
        // user has no packets of their own — their cross-org view is a
        // separate /admin page. No verb on the FE by design: route entry
        // implies `read`, and decide() is authoritative regardless.
        {
          path: "legal",
          Component: LegalDocumentsPage,
          access: { resource: "legal-document" },
        },
        // Cross-org transfer acceptance queue (receiving org admin / facility
        // manager) — app portal only; gated on resident-transfer:accept.
        {
          path: "incoming-transfers",
          Component: IncomingTransfersPage,
          access: { anyOf: ["resident-transfer:accept"] },
        },
      ],
    },

    // US-037: facility-level webhook settings
    { path: "facility-settings/webhooks", Component: WebhooksPage },
    {
      path: "facility-settings/webhooks/:id/deliveries",
      Component: WebhookDeliveriesPage,
    },
  ],
};

// Reused by admin.portal.ts so the admin portal has its own settings subtree
// (platform admins are bounced from the app portal). profileRoute is reused by
// the admin and audit portals so every user can reach their own profile page.
export { settingsRoute, profileRoute };
